// [S-260 + S-262] Ingestion data hooks. Backed by:
//   GET  /admin/documents?state=...&limit=...
//   POST /admin/documents/:id/replay
//   POST /review-queue/:documentHash/approve   (S-072 — embeds → precedent)
//   POST /review-queue/:documentHash/reject
//
// Platform_admin only at the server. The UI hides the entry points
// for non-admin sessions, but the role gate is enforced server-side.
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { getApiErrorMessage, useApi } from "./api";
import { PRECEDENTS_KEY } from "./precedentsQuery";

export type DocumentLifecycleState =
  | "received"
  | "parsing"
  | "stage1_complete"
  | "stage2_review"
  | "stage3_human"
  | "indexed"
  | "failed";

export type IngestionDocumentRow = {
  id: string;
  tenantId: string;
  documentHash: string;
  lifecycleState: DocumentLifecycleState;
  uploadedAt: string; // ISO
  uploadedBy: string;
  // Readable identity for the status board. `filename` is the original upload's
  // basename; the classification fields are auto-detected on ingest (uploader
  // tags override). All nullable — a legacy row or a doc mid-parse may lack them.
  filename?: string | null;
  dealType?: string | null;
  guild?: string | null;
  talentTier?: string | null;
};

export const DOCUMENT_LIFECYCLE_STATES: ReadonlyArray<DocumentLifecycleState> = [
  "received",
  "parsing",
  "stage1_complete",
  "stage2_review",
  "stage3_human",
  "indexed",
  "failed",
] as const;

export const INGESTION_KEY = ["admin", "documents"] as const;

export function useIngestionDocuments(filter: {
  state?: DocumentLifecycleState;
  tenantId?: string;
  limit?: number;
}): UseQueryResult<IngestionDocumentRow[], Error> {
  const api = useApi("admin");
  const params = new URLSearchParams();
  if (filter.state !== undefined) params.set("state", filter.state);
  if (filter.tenantId !== undefined) params.set("tenantId", filter.tenantId);
  if (filter.limit !== undefined) params.set("limit", String(filter.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: [...INGESTION_KEY, filter] as const,
    // keep prior rows visible while a filter/scope change refetches (no blank flash)
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await api<{ documents: IngestionDocumentRow[] }>(
        `/admin/documents${qs.length > 0 ? `?${qs}` : ""}`
      );
      return res.documents;
    },
  });
}

// [S-073a] Review-queue entries (GET /review-queue) — pending Stage-3 docs with
// the auto-detected taxonomy (Stage-2 LLM) so the UI can prefill the per-file
// tag editor. tenantId omitted → server scopes to the caller's studio
// (platform_admin sees all tenants).
export type ReviewQueueEntryRow = {
  documentId: string;
  documentHash: string;
  tenantId: string;
  status: string;
  needsFullHumanExtraction: boolean;
  enqueuedAt: string;
  // [S-631] Original upload filename so a reviewer knows which document they're
  // categorizing. null for legacy docs that predate captured filenames.
  originalFileName: string | null;
  dealType: string | null;
  guild: string | null;
  // [Phase 3b] Auto-detected OPEN dimensions + tier the reviewer can correct.
  talentTier: string | null;
  instrumentType: string | null;
  counterpartyRole: string | null;
  budgetBand: string | null;
};

export const REVIEW_QUEUE_KEY = ["admin", "review-queue"] as const;

export function useReviewQueue(
  tenantId?: string
): UseQueryResult<ReviewQueueEntryRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...REVIEW_QUEUE_KEY, tenantId] as const,
    queryFn: async () => {
      const q =
        tenantId !== undefined && tenantId.length > 0
          ? `?tenantId=${encodeURIComponent(tenantId)}`
          : "";
      const res = await api<{ entries?: ReviewQueueEntryRow[] }>(
        `/review-queue${q}`
      );
      // Degrade to an empty queue rather than throwing on a malformed/empty
      // body (react-query rejects an undefined queryFn result).
      return res.entries ?? [];
    },
  });
}

// Per-file taxonomy correction sent on approve. Only the fields the reviewer
// actually changed should be set; the server applies precedence
// (reviewer > LLM). tags → precedentTerms (dealType/guild/tier).
export type ApproveTags = {
  dealType?: string;
  guild?: string;
  talentTier?: string;
};

// [Phase 3b] Reviewer corrections to the OPEN comparability dimensions
// (→ Contract.dimensions). Sent alongside tags on approve; only changed keys
// are set, and the reviewer value wins over the Stage-2 extraction server-side.
export type ApproveDimensions = {
  instrumentType?: string;
  counterpartyRole?: string;
  budgetBand?: string;
};

// [S-072/S-073a] Approve a Stage-3 entry → builds + EMBEDS the StructuredContract
// into the tenant's L2 vector collection and moves it to `indexed`. This is the
// step that turns an uploaded contract into a searchable precedent. Optional
// `tags` carry the reviewer's per-file taxonomy correction. Invalidates the
// queue + precedents caches so the row leaves the queue and shows up under
// precedents.
export function useApproveReviewEntry(): UseMutationResult<
  { documentId: string; lifecycleState: string; indexed: boolean },
  Error,
  { documentHash: string; tags?: ApproveTags; dimensions?: ApproveDimensions }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentHash, tags, dimensions }) =>
      api<{ documentId: string; lifecycleState: string; indexed: boolean }>(
        `/review-queue/${encodeURIComponent(documentHash)}/approve`,
        { method: "POST", body: { ...(tags ?? {}), ...(dimensions ?? {}) } }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INGESTION_KEY });
      void qc.invalidateQueries({ queryKey: REVIEW_QUEUE_KEY });
      void qc.invalidateQueries({ queryKey: PRECEDENTS_KEY });
    },
  });
}

// [S-072] Reject a Stage-3 entry → moves the doc to `failed` (not indexed).
export function useRejectReviewEntry(): UseMutationResult<
  { rejected: boolean },
  Error,
  { documentHash: string; reason: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentHash, reason }) =>
      api<{ rejected: boolean }>(
        `/review-queue/${encodeURIComponent(documentHash)}/reject`,
        { method: "POST", body: { reason } }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INGESTION_KEY });
      void qc.invalidateQueries({ queryKey: REVIEW_QUEUE_KEY });
    },
  });
}

/** [bulk-retry] Replay MANY failed documents in one action.
 *
 *  Retrying a bad batch one row at a time is not a workflow — the 2026-08-15
 *  demo left 109 documents needing recovery. Runs the SAME per-document replay
 *  endpoint (which re-checks ingestion:operate and the document's own state)
 *  SEQUENTIALLY: the pipeline is capped at 4 concurrent parses, and firing a
 *  hundred replays at once is the burst shape that OOMed it in the first place.
 *  Reports per-document outcomes rather than failing the whole run on one bad
 *  document. */
export function useReplayDocuments(): UseMutationResult<
  { ok: string[]; failed: Array<{ documentId: string; message: string }> },
  Error,
  {
    documentIds: string[];
    fromStage?: "1" | "2" | "3";
    /** Called after each document settles, so the caller can show REAL
     *  progress. A spinner that doesn't track the work is a lie about a run
     *  that can take a minute. */
    onProgress?: (settled: number, total: number) => void;
  }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentIds, fromStage = "1", onProgress }) => {
      const ok: string[] = [];
      const failed: Array<{ documentId: string; message: string }> = [];
      for (const documentId of documentIds) {
        try {
          await api(
            `/admin/documents/${encodeURIComponent(documentId)}/replay`,
            { method: "POST", body: { fromStage } }
          );
          ok.push(documentId);
        } catch (err) {
          failed.push({
            documentId,
            message: getApiErrorMessage(err) ?? "replay failed",
          });
        }
        onProgress?.(ok.length + failed.length, documentIds.length);
      }
      return { ok, failed };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INGESTION_KEY });
    },
  });
}

export function useReplayDocument(): UseMutationResult<
  { documentId: string; stage: string },
  Error,
  { documentId: string; fromStage?: "1" | "2" | "3" }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, fromStage = "1" }) =>
      api<{ documentId: string; stage: string }>(
        `/admin/documents/${encodeURIComponent(documentId)}/replay`,
        { method: "POST", body: { fromStage } }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INGESTION_KEY });
    },
  });
}
